"use client";

import { useState } from "react";
import { siteConfig } from "@/config/site";
import { MessageSquare, Mail, Send, CheckCircle2, AlertCircle } from "lucide-react";

export function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");

    try {
      const form = e.currentTarget;
      const formData = new FormData(form);

      const res = await fetch(siteConfig.contactEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.fromEntries(formData)),
      });

      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Info Side */}
          <div className="lg:col-span-5">
            <h2 className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
              Get in Touch
            </h2>
            <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              Book a free engineering consultation.
            </h3>
            <p className="mt-4 text-slate-600 leading-relaxed text-sm">
              Discuss your cloud architecture, deployment pipelines, or security requirements with an experienced cloud architect. No sales fluff—just technical feedback on your workload.
            </p>

            <div className="mt-8 space-y-4">
              {/* WhatsApp direct */}
              <a
                href={siteConfig.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3.5 bg-white border border-gray-200 rounded-md hover:border-emerald-500 transition-colors group"
              >
                <div className="p-2 bg-emerald-50 rounded text-emerald-600">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">WhatsApp Direct</div>
                  <div className="text-sm font-semibold text-slate-900 group-hover:text-emerald-600">
                    {siteConfig.whatsappDisplay}
                  </div>
                </div>
              </a>

              {/* Configurable Email Option */}
              {siteConfig.email && (
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex items-center gap-3 p-3.5 bg-white border border-gray-200 rounded-md hover:border-blue-500 transition-colors group"
                >
                  <div className="p-2 bg-blue-50 rounded text-blue-600">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 font-medium">Email Us</div>
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-600">
                      {siteConfig.email}
                    </div>
                  </div>
                </a>
              )}
            </div>
          </div>

          {/* Form Side */}
          <div className="lg:col-span-7 bg-white border border-gray-200 p-6 sm:p-8 rounded-md shadow-sm">
            {status === "success" ? (
              <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-md text-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                <h4 className="text-lg font-bold text-slate-900">Message Received</h4>
                <p className="text-sm text-slate-600 mt-1">
                  Thanks — your message has been received. We&apos;ll get back to you shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-xs font-semibold text-blue-600 hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company" className="block text-xs font-semibold text-slate-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="website" className="block text-xs font-semibold text-slate-700 mb-1">
                      Website (Optional)
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="details" className="block text-xs font-semibold text-slate-700 mb-1">
                    What do you need help with? *
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    rows={4}
                    required
                    placeholder="Describe your current cloud environment or project goals..."
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  ></textarea>
                </div>

                {status === "error" && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>Failed to submit. Please try again or reach us on WhatsApp.</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-slate-900 hover:bg-blue-600 rounded transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  <span>{status === "loading" ? "Submitting..." : "Request Free Consultation"}</span>
                </button>
              </form>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
